import React from 'react';

import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'components/ui/use-toast';
import { notifications } from 'services/api';

const POLL_INTERVAL_MS = 60_000;

const formatMeetingStart = (startIso: string | null, timeZone: string): string | null => {
  if (!startIso) return null;
  const parsed = new Date(startIso);
  if (Number.isNaN(parsed.getTime())) return null;

  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
      timeZoneName: 'short',
    }).format(parsed);
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(parsed);
  }
};

export const InAppMeetingReminderPoller: React.FC = () => {
  const { isAuthenticated, isAuthLoading, user } = useAuth();
  const userId = user?.id;
  const { addToast } = useToast();
  const processedRef = React.useRef<Set<string>>(new Set());
  const processedUserRef = React.useRef(userId);

  const getStatusCode = (error: unknown): number | null => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as { response?: { status?: unknown } }).response?.status === 'number'
    ) {
      return (error as { response?: { status?: number } }).response?.status ?? null;
    }
    return null;
  };

  React.useEffect(() => {
    if (!isAuthenticated || processedUserRef.current !== userId) {
      processedRef.current = new Set();
      processedUserRef.current = userId;
    }
    // AuthContext validates both cookie-restored and freshly signed-in sessions.
    if (!isAuthenticated || isAuthLoading || userId == null) {
      return;
    }

    const processed = processedRef.current;
    let cancelled = false;
    let inFlight = false;
    let unauthorized = false;
    let handle: number | null = null;

    const stopPolling = () => {
      unauthorized = true;
      if (handle !== null) {
        window.clearInterval(handle);
        handle = null;
      }
    };

    const poll = async () => {
      if (cancelled || unauthorized || inFlight) {
        return;
      }
      inFlight = true;

      try {
        const due = await notifications.getDueInAppNotifications();
        if (cancelled || !Array.isArray(due) || due.length === 0) {
          return;
        }

        for (const item of due) {
          // Acknowledgement awaits can cross logout or an account change.
          if (cancelled) return;
          if (!item?.id || processed.has(item.id)) {
            continue;
          }
          processed.add(item.id);
          if (item.type === 'mentorship_recap_ready') {
            const from = item.meeting?.title || 'your session';
            const counterpart = item.record?.counterpart_name ? ` with ${item.record.counterpart_name}` : '';
            const recordPath = item.record?.relationship_id
              ? `/mentorship?record=${encodeURIComponent(item.record.relationship_id)}`
              : '/mentorship';
            addToast({
              title: 'Your For next time notes are ready',
              description: `From ${from}${counterpart}. Review the recap, the topics for next time, and anything you agreed to do.`,
              action: (
                <a href={recordPath} className="text-sm font-semibold underline underline-offset-2">
                  Open notes
                </a>
              ),
              persistent: true,
            });
          } else {
            const meetingTitle = item.meeting?.title || 'Upcoming meeting';
            const formattedStart = formatMeetingStart(
              item.meeting?.start_time ?? null,
              item.meeting?.time_zone || 'UTC',
            );
            addToast({
              title: `Meeting Reminder: ${meetingTitle}`,
              description: formattedStart
                ? `Start time: ${formattedStart}`
                : 'Start time: starting soon',
              persistent: true,
            });
          }
          try {
            await notifications.acknowledgeInAppNotification(item.id);
          } catch {
            // Keep local dedupe to avoid repeated toasts in this session.
          }
        }
      } catch (error) {
        if (getStatusCode(error) === 401) {
          stopPolling();
          return;
        }
        // Silent failure to avoid disrupting user workflows.
      } finally {
        inFlight = false;
      }
    };

    void poll();
    handle = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (handle !== null) {
        window.clearInterval(handle);
      }
    };
  }, [addToast, isAuthenticated, isAuthLoading, userId]);

  return null;
};
